import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Globe, RotateCcw, Star, Target, Info, Check, X, MapPin } from 'lucide-react';
import { useLabProgress } from '../../hooks/useLabProgress';
import { geoMercator, geoPath, geoEquirectangular } from 'd3-geo';
import { feature } from 'topojson-client';
import worldTopology from 'world-atlas/countries-110m.json';

// Real-world cartography (Natural Earth 110m) - India + world.
const _COUNTRIES = feature(worldTopology, worldTopology.objects.countries).features;
const _INDIA = _COUNTRIES.find(f => (f.id === '356') || (f.properties && f.properties.name === 'India'));
// India Mercator projection fitted to a 600x520 canvas so state markers align.
const INDIA_W = 600, INDIA_H = 520;
const INDIA_PROJ = _INDIA ? geoMercator().fitSize([INDIA_W, INDIA_H - 20], _INDIA) : null;
const INDIA_PATH = INDIA_PROJ ? geoPath(INDIA_PROJ)(_INDIA) : '';
// World Equirectangular for the world-continents mission.
const WORLD_W = 600, WORLD_H = 520;
const WORLD_PROJ = geoEquirectangular().scale(WORLD_H / Math.PI).translate([WORLD_W / 2, WORLD_H / 2]);
const WORLD_PATHS = _COUNTRIES.map((f, i) => ({ id: f.id || i, name: (f.properties||{}).name, d: geoPath(WORLD_PROJ)(f) })).filter(c => c.d);



// Simplified India map state positions (approximate x,y on 600x650 canvas)
const INDIA_STATES = [
  { id: 'jk', name: 'Jammu & Kashmir', x: 210, y: 45, capital: 'Srinagar' },
  { id: 'hp', name: 'Himachal Pradesh', x: 230, y: 95, capital: 'Shimla' },
  { id: 'pb', name: 'Punjab', x: 195, y: 110, capital: 'Chandigarh' },
  { id: 'uk', name: 'Uttarakhand', x: 270, y: 110, capital: 'Dehradun' },
  { id: 'hr', name: 'Haryana', x: 210, y: 140, capital: 'Chandigarh' },
  { id: 'dl', name: 'Delhi', x: 230, y: 145, capital: 'New Delhi' },
  { id: 'rj', name: 'Rajasthan', x: 165, y: 190, capital: 'Jaipur' },
  { id: 'up', name: 'Uttar Pradesh', x: 300, y: 170, capital: 'Lucknow' },
  { id: 'br', name: 'Bihar', x: 380, y: 190, capital: 'Patna' },
  { id: 'mp', name: 'Madhya Pradesh', x: 255, y: 250, capital: 'Bhopal' },
  { id: 'gj', name: 'Gujarat', x: 130, y: 260, capital: 'Gandhinagar' },
  { id: 'mh', name: 'Maharashtra', x: 200, y: 320, capital: 'Mumbai' },
  { id: 'goa', name: 'Goa', x: 165, y: 370, capital: 'Panaji' },
  { id: 'ka', name: 'Karnataka', x: 195, y: 400, capital: 'Bengaluru' },
  { id: 'kl', name: 'Kerala', x: 195, y: 470, capital: 'Thiruvananthapuram' },
  { id: 'tn', name: 'Tamil Nadu', x: 240, y: 460, capital: 'Chennai' },
  { id: 'ap', name: 'Andhra Pradesh', x: 260, y: 380, capital: 'Amaravati' },
  { id: 'ts', name: 'Telangana', x: 255, y: 340, capital: 'Hyderabad' },
  { id: 'od', name: 'Odisha', x: 350, y: 300, capital: 'Bhubaneswar' },
  { id: 'wb', name: 'West Bengal', x: 400, y: 240, capital: 'Kolkata' },
  { id: 'jh', name: 'Jharkhand', x: 370, y: 230, capital: 'Ranchi' },
  { id: 'ch', name: 'Chhattisgarh', x: 315, y: 280, capital: 'Raipur' },
  { id: 'as', name: 'Assam', x: 470, y: 170, capital: 'Dispur' },
  { id: 'sk', name: 'Sikkim', x: 420, y: 165, capital: 'Gangtok' },
  { id: 'ne', name: 'NE States', x: 480, y: 200, capital: 'Various' },
];

const WORLD_ITEMS = [
  { id: 'asia', name: 'Asia', x: 420, y: 180, type: 'continent' },
  { id: 'europe', name: 'Europe', x: 310, y: 120, type: 'continent' },
  { id: 'africa', name: 'Africa', x: 300, y: 280, type: 'continent' },
  { id: 'namerica', name: 'North America', x: 130, y: 150, type: 'continent' },
  { id: 'samerica', name: 'South America', x: 180, y: 340, type: 'continent' },
  { id: 'australia', name: 'Australia', x: 480, y: 370, type: 'continent' },
  { id: 'antarctica', name: 'Antarctica', x: 300, y: 460, type: 'continent' },
];

const RIVERS = [
  { id: 'ganga', name: 'Ganga', points: [[270,130],[300,170],[350,190],[400,220]] },
  { id: 'yamuna', name: 'Yamuna', points: [[230,145],[250,170],[270,200],[280,230]] },
  { id: 'godavari', name: 'Godavari', points: [[255,330],[290,340],[330,330],[350,310]] },
  { id: 'krishna', name: 'Krishna', points: [[220,360],[260,370],[300,360],[340,340]] },
  { id: 'narmada', name: 'Narmada', points: [[175,270],[220,265],[260,260],[300,265]] },
];

const MIXED_QUESTIONS = [
  { q: 'Which is the longest river in India?', options: ['Ganga', 'Godavari', 'Narmada', 'Yamuna'], answer: 'Ganga' },
  { q: 'Which state has the largest area?', options: ['Rajasthan', 'Maharashtra', 'MP', 'UP'], answer: 'Rajasthan' },
  { q: 'Capital of Karnataka?', options: ['Chennai', 'Bengaluru', 'Hyderabad', 'Kochi'], answer: 'Bengaluru' },
  { q: 'Largest continent by area?', options: ['Africa', 'Asia', 'Europe', 'N. America'], answer: 'Asia' },
  { q: 'Which ocean is east of India?', options: ['Pacific', 'Atlantic', 'Bay of Bengal', 'Arabian Sea'], answer: 'Bay of Bengal' },
  { q: 'Highest peak in India?', options: ['K2', 'Kangchenjunga', 'Nanda Devi', 'Everest'], answer: 'Kangchenjunga' },
  { q: 'Which country borders India to the north-east?', options: ['Pakistan', 'Myanmar', 'Sri Lanka', 'Nepal'], answer: 'Myanmar' },
  { q: 'Which mountain range runs along India\'s northern border?', options: ['Aravalli', 'Western Ghats', 'Himalayas', 'Vindhyas'], answer: 'Himalayas' },
  { q: 'India has which type of climate?', options: ['Tropical', 'Monsoon', 'Mediterranean', 'Arctic'], answer: 'Monsoon' },
  { q: 'Where is the Taj Mahal located?', options: ['Delhi', 'Agra', 'Jaipur', 'Mumbai'], answer: 'Agra' },
  { q: 'Which is the smallest state by area?', options: ['Goa', 'Sikkim', 'Tripura', 'Mizoram'], answer: 'Goa' },
  { q: 'Which desert is in western India?', options: ['Sahara', 'Gobi', 'Thar', 'Kalahari'], answer: 'Thar' },
  { q: 'How many countries share border with India?', options: ['5', '6', '7', '9'], answer: '7' },
  { q: 'Which plateau is the oldest landmass in India?', options: ['Deccan', 'Malwa', 'Chota Nagpur', 'Meghalaya'], answer: 'Deccan' },
  { q: 'Gateway of India is in which city?', options: ['Delhi', 'Kolkata', 'Mumbai', 'Chennai'], answer: 'Mumbai' },
  { q: 'Which state is known as the Spice Garden of India?', options: ['Tamil Nadu', 'Kerala', 'Karnataka', 'Goa'], answer: 'Kerala' },
];

const NEIGHBOUR_QUESTIONS = [
  { q: 'Which country lies to the immediate north of India, across the Himalayas?', options: ['China', 'Russia', 'Mongolia', 'Kazakhstan'], answer: 'China' },
  { q: 'Which neighbour shares the longest land border with India?', options: ['Pakistan', 'Bangladesh', 'Nepal', 'China'], answer: 'Bangladesh' },
  { q: 'Which country almost entirely surrounds Bangladesh?', options: ['India', 'Myanmar', 'Nepal', 'Bhutan'], answer: 'India' },
  { q: 'Which Himalayan kingdom, the "Land of the Thunder Dragon", borders India\'s northeast?', options: ['Nepal', 'Bhutan', 'Tibet', 'Sikkim'], answer: 'Bhutan' },
  { q: 'Which country borders India\'s northeast states of Mizoram and Manipur?', options: ['Myanmar', 'Bangladesh', 'China', 'Thailand'], answer: 'Myanmar' },
  { q: 'Kathmandu is the capital of which Himalayan neighbour of India?', options: ['Bhutan', 'Nepal', 'Tibet', 'Sikkim'], answer: 'Nepal' },
  { q: 'Which country borders India near Jammu & Kashmir, close to the Wakhan corridor?', options: ['Afghanistan', 'Tajikistan', 'Iran', 'Turkmenistan'], answer: 'Afghanistan' },
  { q: 'How many countries share a land border with India?', options: ['5', '6', '7', '9'], answer: '7' },
  { q: 'Which country lies west of India, across the Radcliffe Line?', options: ['Iran', 'Pakistan', 'Afghanistan', 'Oman'], answer: 'Pakistan' },
  { q: 'Sri Lanka is separated from India by which strait?', options: ['Palk Strait', 'Malacca Strait', 'Strait of Hormuz', 'Bering Strait'], answer: 'Palk Strait' },
];

const MOUNTAIN_QUESTIONS = [
  { q: 'Which mountain range forms India\'s northern border and holds its highest peaks?', options: ['Aravalli', 'Himalayas', 'Vindhya', 'Western Ghats'], answer: 'Himalayas' },
  { q: 'Which is the oldest mountain range in India?', options: ['Himalayas', 'Aravalli', 'Satpura', 'Nilgiri'], answer: 'Aravalli' },
  { q: 'Which mountain range runs along India\'s west coast?', options: ['Eastern Ghats', 'Western Ghats', 'Vindhya', 'Satpura'], answer: 'Western Ghats' },
  { q: 'Which discontinuous range runs along India\'s east coast?', options: ['Western Ghats', 'Eastern Ghats', 'Aravalli', 'Karakoram'], answer: 'Eastern Ghats' },
  { q: 'Which range separates the Narmada and Tapi rivers?', options: ['Vindhya-Satpura', 'Aravalli', 'Nilgiri', 'Karakoram'], answer: 'Vindhya-Satpura' },
  { q: 'K2, the world\'s second-highest peak, lies in which range?', options: ['Himalayas', 'Karakoram', 'Hindu Kush', 'Aravalli'], answer: 'Karakoram' },
  { q: 'Kangchenjunga, India\'s highest peak, belongs to which range?', options: ['Karakoram', 'Himalayas', 'Aravalli', 'Western Ghats'], answer: 'Himalayas' },
  { q: 'The Nilgiri Hills (Ooty) lie at the junction of which two ranges?', options: ['Eastern & Western Ghats', 'Himalayas & Aravalli', 'Vindhya & Satpura', 'Karakoram & Himalayas'], answer: 'Eastern & Western Ghats' },
  { q: 'Which range is the source of rivers like the Narmada and Son?', options: ['Satpura/Maikal', 'Aravalli', 'Western Ghats', 'Himalayas'], answer: 'Satpura/Maikal' },
  { q: 'Mount Abu, Rajasthan\'s hill station, sits in which range?', options: ['Aravalli', 'Vindhya', 'Nilgiri', 'Satpura'], answer: 'Aravalli' },
];

const CLIMATE_QUESTIONS = [
  { q: 'India\'s overall climate, driven by seasonal winds, is called?', options: ['Mediterranean', 'Monsoon', 'Arctic', 'Desert'], answer: 'Monsoon' },
  { q: 'Which region has a hot desert climate with very low rainfall?', options: ['Thar Desert (Rajasthan)', 'Kerala', 'Assam', 'Kashmir'], answer: 'Thar Desert (Rajasthan)' },
  { q: 'Which climate type is found at high altitudes in the Himalayas?', options: ['Alpine', 'Tropical', 'Arid', 'Equatorial'], answer: 'Alpine' },
  { q: 'Which coastal state first receives the southwest monsoon?', options: ['Kerala', 'Punjab', 'Rajasthan', 'Haryana'], answer: 'Kerala' },
  { q: 'What is the October-November season in India called?', options: ['Winter', 'Post-monsoon (retreating monsoon)', 'Summer', 'Monsoon'], answer: 'Post-monsoon (retreating monsoon)' },
  { q: 'Cherrapunji / Mawsynram, among the wettest places on Earth, are in which state?', options: ['Meghalaya', 'Kerala', 'Assam', 'Sikkim'], answer: 'Meghalaya' },
  { q: 'Which climate describes most of peninsular India (hot summers, mild winters, seasonal rain)?', options: ['Tropical savanna', 'Polar', 'Mediterranean', 'Continental'], answer: 'Tropical savanna' },
  { q: 'The Coromandel Coast (Tamil Nadu) gets most rain from which monsoon?', options: ['Northeast (retreating) monsoon', 'Southwest monsoon', 'Western disturbances', 'None'], answer: 'Northeast (retreating) monsoon' },
  { q: 'Why does Ladakh have a cold desert climate?', options: ['High altitude blocks moisture', 'Coastal location', 'Equatorial position', 'Ocean currents'], answer: 'High altitude blocks moisture' },
  { q: 'Western disturbances bring winter rain/snow mainly to which region?', options: ['Northwest India (J&K, Himachal, Punjab)', 'Kerala', 'Tamil Nadu', 'Odisha'], answer: 'Northwest India (J&K, Himachal, Punjab)' },
];

const LANDMARK_QUESTIONS = [
  { q: 'The Taj Mahal is located in which city?', options: ['Agra', 'Delhi', 'Jaipur', 'Lucknow'], answer: 'Agra' },
  { q: 'Gateway of India overlooks the sea in which city?', options: ['Mumbai', 'Chennai', 'Goa', 'Kochi'], answer: 'Mumbai' },
  { q: 'India Gate, a war memorial, is located in which city?', options: ['Delhi', 'Mumbai', 'Kolkata', 'Jaipur'], answer: 'Delhi' },
  { q: 'Charminar, a 16th-century monument, is in which city?', options: ['Hyderabad', 'Bengaluru', 'Chennai', 'Pune'], answer: 'Hyderabad' },
  { q: 'The Golden Temple (Harmandir Sahib) is located in which city?', options: ['Amritsar', 'Chandigarh', 'Ludhiana', 'Patiala'], answer: 'Amritsar' },
  { q: 'Hawa Mahal, the "Palace of Winds", is found in which city?', options: ['Jaipur', 'Udaipur', 'Jodhpur', 'Bikaner'], answer: 'Jaipur' },
  { q: 'Which coastal Odisha temple is famous for its chariot-wheel architecture?', options: ['Konark Sun Temple', 'Jagannath Temple Puri', 'Lingaraj Temple', 'Meenakshi Temple'], answer: 'Konark Sun Temple' },
  { q: 'Victoria Memorial, a marble monument, stands in which city?', options: ['Kolkata', 'Mumbai', 'Chennai', 'Delhi'], answer: 'Kolkata' },
  { q: 'Qutub Minar, the tallest brick minaret in the world, is in which city?', options: ['Delhi', 'Agra', 'Lucknow', 'Jaipur'], answer: 'Delhi' },
  { q: 'Mysore Palace, a royal residence, is located in which state?', options: ['Karnataka', 'Tamil Nadu', 'Kerala', 'Andhra Pradesh'], answer: 'Karnataka' },
];

const ULTIMATE_QUESTIONS = [...MIXED_QUESTIONS, ...NEIGHBOUR_QUESTIONS, ...MOUNTAIN_QUESTIONS, ...CLIMATE_QUESTIONS, ...LANDMARK_QUESTIONS];

// Each quiz mission (M5-M10) draws from its own topic-specific bank so
// missions never repeat the same generic question set.
const QUIZ_BANKS = {
  5: MIXED_QUESTIONS,
  6: NEIGHBOUR_QUESTIONS,
  7: MOUNTAIN_QUESTIONS,
  8: CLIMATE_QUESTIONS,
  9: LANDMARK_QUESTIONS,
  10: ULTIMATE_QUESTIONS,
};

const MISSIONS = [
  { id: 1, name: 'Indian States', description: 'Click on the correct state when prompted', type: 'identify-state', stars: 1 },
  { id: 2, name: 'State Capitals', description: 'Match states with their capitals', type: 'match-capitals', stars: 2 },
  { id: 3, name: 'Indian Rivers', description: 'Identify major rivers of India', type: 'identify-rivers', stars: 2 },
  { id: 4, name: 'World Continents', description: 'Identify all 7 continents on the world map', type: 'world-continents', stars: 1 },
  { id: 5, name: 'Mixed Challenge', description: 'Answer random geography questions', type: 'quiz', stars: 3 },
  { id: 6, name: 'Neighbouring Countries', description: 'Identify countries that share a border with India', type: 'quiz', stars: 2 },
  { id: 7, name: 'Mountain Ranges', description: 'Identify major mountain ranges of India', type: 'quiz', stars: 2 },
  { id: 8, name: 'Indian Climate Zones', description: 'Match regions with their climate type', type: 'quiz', stars: 2 },
  { id: 9, name: 'Famous Landmarks', description: 'Match Indian landmarks to their states', type: 'quiz', stars: 3 },
  { id: 10, name: 'Ultimate Geography Master', description: 'Mixed questions from all categories - the final challenge!', type: 'quiz', stars: 3 },
];

export default function GeographyLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('geography');
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('geography', 10);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [answered, setAnswered] = useState([]);
  const [matchState, setMatchState] = useState({ states: [], selected: null, pairs: [] });

  const mission = MISSIONS[currentMission] || MISSIONS[0];
  const sortedCapitals = useMemo(
    () => [...(matchState.states || [])].sort((a, b) => a.capital.localeCompare(b.capital)),
    [matchState.states]
  );

  const generateQuestion = useCallback(() => {
    if (mission.type === 'identify-state') {
      const remaining = INDIA_STATES.filter(s => !answered.includes(s.id));
      if (remaining.length === 0) return null;
      const state = remaining[Math.floor(Math.random() * remaining.length)];
      return { type: 'click', target: state, prompt: `Click on: ${state.name}` };
    }
    if (mission.type === 'identify-rivers') {
      const remaining = RIVERS.filter(r => !answered.includes(r.id));
      if (remaining.length === 0) return null;
      const river = remaining[Math.floor(Math.random() * remaining.length)];
      return { type: 'click-river', target: river, prompt: `Click on the ${river.name} river` };
    }
    if (mission.type === 'world-continents') {
      const remaining = WORLD_ITEMS.filter(w => !answered.includes(w.id));
      if (remaining.length === 0) return null;
      const item = remaining[Math.floor(Math.random() * remaining.length)];
      return { type: 'click-world', target: item, prompt: `Click on: ${item.name}` };
    }
    if (mission.type === 'quiz') {
      const bank = QUIZ_BANKS[mission.id] || MIXED_QUESTIONS;
      const remaining = bank.filter((_, i) => !answered.includes(`m${mission.id}-q${i}`));
      if (remaining.length === 0) return null;
      const idx = Math.floor(Math.random() * remaining.length);
      const item = remaining[idx];
      return { type: 'quiz', ...item, qid: `m${mission.id}-q${bank.indexOf(item)}` };
    }
    return null;
  }, [mission, answered]);

  useEffect(() => {
    setScore(0);
    setTotalQuestions(0);
    setAnswered([]);
    setFeedback(null);
    setMatchState({ states: [], selected: null, pairs: [] });
    if (mission.type === 'match-capitals') {
      const shuffledStates = [...INDIA_STATES].sort(() => Math.random() - 0.5).slice(0, 8);
      setMatchState({ states: shuffledStates, selected: null, pairs: [] });
    }
  }, [currentMission, mission.type]);

  useEffect(() => {
    if (mission.type !== 'match-capitals') {
      const q = generateQuestion();
      setCurrentQuestion(q);
    }
  }, [answered, mission.type, generateQuestion]);

  const drawMap = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = '#0c1220';
    ctx.fillRect(0, 0, w, h);

    const isWorldMode = mission.type === 'world-continents';

    if (isWorldMode) {
      // World map with real Natural Earth country outlines
      ctx.fillStyle = '#0c1e3a';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(148,197,255,0.35)';
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 0.6;
      const sx = w / WORLD_W, sy = h / WORLD_H;
      WORLD_PATHS.forEach(c => {
        try {
          const p = new Path2D(c.d);
          ctx.save(); ctx.scale(sx, sy);
          ctx.fill(p); ctx.stroke(p);
          ctx.restore();
        } catch (_) {}
      });
      WORLD_ITEMS.forEach(item => {
        const isHover = hoveredItem === item.id;
        const isFound = answered.includes(item.id);
        ctx.beginPath();
        ctx.arc(item.x, item.y, isHover ? 35 : 30, 0, Math.PI * 2);
        ctx.fillStyle = isFound ? 'rgba(34,197,94,0.35)' : isHover ? 'rgba(96,165,250,0.35)' : 'rgba(96,165,250,0.15)';
        ctx.fill();
        ctx.strokeStyle = isFound ? '#22c55e' : isHover ? '#60a5fa' : 'rgba(96,165,250,0.4)';
        ctx.lineWidth = isHover ? 2 : 1;
        ctx.stroke();
        ctx.fillStyle = isFound ? '#86efac' : '#e2e8f0';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(item.name, item.x, item.y + 4);
      });
    } else {
      // India background with real Natural Earth outline of India.
      ctx.fillStyle = '#0c1220';
      ctx.fillRect(0, 0, w, h);
      if (INDIA_PATH) {
        try {
          const p = new Path2D(INDIA_PATH);
          ctx.save();
          ctx.scale(w / INDIA_W, h / INDIA_H);
          ctx.fillStyle = 'rgba(59,130,246,0.10)';
          ctx.strokeStyle = 'rgba(148,197,255,0.55)';
          ctx.lineWidth = 1.4;
          ctx.fill(p); ctx.stroke(p);
          ctx.restore();
        } catch (_) {}
      }

      // Rivers
      if (mission.type === 'identify-rivers') {
        RIVERS.forEach(river => {
          const isHover = hoveredItem === river.id;
          const isFound = answered.includes(river.id);
          ctx.beginPath();
          ctx.moveTo(river.points[0][0], river.points[0][1]);
          for (let i = 1; i < river.points.length; i++) {
            ctx.lineTo(river.points[i][0], river.points[i][1]);
          }
          ctx.strokeStyle = isFound ? '#22c55e' : isHover ? '#60a5fa' : 'rgba(59,130,246,0.4)';
          ctx.lineWidth = isHover ? 4 : 3;
          ctx.stroke();
          // Label
          const mid = river.points[Math.floor(river.points.length / 2)];
          ctx.fillStyle = isFound ? '#86efac' : '#64748b';
          ctx.font = '10px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(river.name, mid[0], mid[1] - 8);
        });
      }

      // States
      INDIA_STATES.forEach(state => {
        const isHover = hoveredItem === state.id;
        const isFound = answered.includes(state.id);
        const r = isHover ? 14 : 10;

        ctx.beginPath();
        ctx.arc(state.x, state.y, r, 0, Math.PI * 2);
        ctx.fillStyle = isFound ? 'rgba(34,197,94,0.4)' : isHover ? 'rgba(96,165,250,0.4)' : 'rgba(96,165,250,0.15)';
        ctx.fill();
        ctx.strokeStyle = isFound ? '#22c55e' : isHover ? '#60a5fa' : 'rgba(96,165,250,0.3)';
        ctx.lineWidth = isHover ? 2 : 1;
        ctx.stroke();

        // State name
        if (isHover || isFound) {
          ctx.fillStyle = isFound ? '#86efac' : '#e2e8f0';
          ctx.font = 'bold 10px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(state.name, state.x, state.y - 16);
        }

        // Dot
        ctx.beginPath();
        ctx.arc(state.x, state.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = isFound ? '#22c55e' : '#60a5fa';
        ctx.fill();
      });
    }
  }, [hoveredItem, answered, mission.type]);

  useEffect(() => { drawMap(); }, [drawMap]);

  const handleCanvasClick = (e) => {
    if (!currentQuestion) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    if (currentQuestion.type === 'click') {
      const target = currentQuestion.target;
      const dist = Math.sqrt((mx - target.x) ** 2 + (my - target.y) ** 2);
      if (dist < 20) {
        setScore(s => s + 1);
        setAnswered(prev => [...prev, target.id]);
        setFeedback({ type: 'correct', text: `Correct! That's ${target.name}` });
        setTotalQuestions(t => t + 1);
      } else {
        // Check which state was clicked
        const clicked = INDIA_STATES.find(s => Math.sqrt((mx - s.x) ** 2 + (my - s.y) ** 2) < 20);
        setFeedback({ type: 'incorrect', text: clicked ? `That's ${clicked.name}, not ${target.name}` : 'No state there. Try again!' });
        setTotalQuestions(t => t + 1);
      }
    } else if (currentQuestion.type === 'click-river') {
      // Check proximity to river path
      const river = currentQuestion.target;
      let minDist = Infinity;
      for (const [px, py] of river.points) {
        const d = Math.sqrt((mx - px) ** 2 + (my - py) ** 2);
        if (d < minDist) minDist = d;
      }
      if (minDist < 30) {
        setScore(s => s + 1);
        setAnswered(prev => [...prev, river.id]);
        setFeedback({ type: 'correct', text: `Correct! That's the ${river.name}` });
      } else {
        setFeedback({ type: 'incorrect', text: 'Not quite. Try clicking closer to the river.' });
      }
      setTotalQuestions(t => t + 1);
    } else if (currentQuestion.type === 'click-world') {
      const target = currentQuestion.target;
      const dist = Math.sqrt((mx - target.x) ** 2 + (my - target.y) ** 2);
      if (dist < 35) {
        setScore(s => s + 1);
        setAnswered(prev => [...prev, target.id]);
        setFeedback({ type: 'correct', text: `Correct! That's ${target.name}` });
      } else {
        setFeedback({ type: 'incorrect', text: 'Try again!' });
      }
      setTotalQuestions(t => t + 1);
    }

    setTimeout(() => setFeedback(null), 2000);
    checkCompletion();
  };

  const handleCanvasMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    if (mission.type === 'world-continents') {
      const item = WORLD_ITEMS.find(w => Math.sqrt((mx - w.x) ** 2 + (my - w.y) ** 2) < 35);
      setHoveredItem(item?.id || null);
    } else if (mission.type === 'identify-rivers') {
      let closest = null;
      let minD = Infinity;
      RIVERS.forEach(r => {
        for (const [px, py] of r.points) {
          const d = Math.sqrt((mx - px) ** 2 + (my - py) ** 2);
          if (d < minD) { minD = d; closest = r; }
        }
      });
      setHoveredItem(minD < 30 ? closest?.id : null);
    } else {
      const state = INDIA_STATES.find(s => Math.sqrt((mx - s.x) ** 2 + (my - s.y) ** 2) < 18);
      setHoveredItem(state?.id || null);
    }
  };

  const handleQuizAnswer = (answer) => {
    if (answer === currentQuestion.answer) {
      setScore(s => s + 1);
      setFeedback({ type: 'correct', text: 'Correct!' });
      setAnswered(prev => [...prev, currentQuestion.qid]);
    } else {
      setFeedback({ type: 'incorrect', text: `Wrong. The answer is ${currentQuestion.answer}` });
    }
    setTotalQuestions(t => t + 1);
    setTimeout(() => setFeedback(null), 2000);
    checkCompletion();
  };

  const handleCapitalMatch = (stateId, isCapitalButton = false) => {
    // State button click - toggle selection (only when no capital click).
    if (!isCapitalButton) {
      if (matchState.selected === stateId) {
        setMatchState(prev => ({ ...prev, selected: null }));
      } else if (!answered.includes(stateId)) {
        setMatchState(prev => ({ ...prev, selected: stateId }));
      }
      return;
    }
    // Capital button click - only fires when a state is selected.
    if (!matchState.selected) return;
    const correct = stateId === matchState.selected;
    if (correct) {
      setMatchState(prev => ({
        ...prev,
        selected: null,
        pairs: [...prev.pairs, { stateId: matchState.selected, capitalId: stateId }],
      }));
      setScore(s => s + 1);
      setTotalQuestions(t => t + 1);
      setAnswered(prev => [...prev, matchState.selected]);
      setFeedback({ type: 'correct', text: 'Correct match!' });
    } else {
      setTotalQuestions(t => t + 1);
      const capName = (matchState.states.find(s => s.id === matchState.selected) || {}).capital;
      setFeedback({ type: 'incorrect', text: `Not quite. That capital belongs to another state. ${capName ? `Try again for ${(matchState.states.find(s => s.id === matchState.selected) || {}).name}.` : ''}` });
    }
    setTimeout(() => setFeedback(null), 1800);
    checkCompletion();
  };

  const checkCompletion = () => {
    const limits = { 'identify-state': 8, 'match-capitals': 8, 'identify-rivers': 5, 'world-continents': 7, 'quiz': 6 };
    const limit = limits[mission.type] || 5;
    if (answered.length + 1 >= limit && !completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
    }
  };

  const resetMission = () => {
    setScore(0);
    setTotalQuestions(0);
    setAnswered([]);
    setFeedback(null);
    setHoveredItem(null);
    if (mission.type === 'match-capitals') {
      const shuffled = [...INDIA_STATES].sort(() => Math.random() - 0.5).slice(0, 8);
      setMatchState({ states: shuffled, selected: null, pairs: [] });
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="geography-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Geography lab exploring India and the world. Identify states, capitals, rivers, and landforms interactively on a map."
        cacheKey="lab-intro-geography"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-400" />
              <span className="font-semibold">Geography Explorer</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { setCurrentMission(i); }} data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) && <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />}
                M{m.id}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
          <Target className="w-5 h-5 text-green-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <Badge variant="secondary" className="text-xs ml-auto">{score}/{totalQuestions}</Badge>
          <div className="flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map / Quiz Area */}
          <div className="lg:col-span-2">
            {mission.type === 'quiz' ? (
              <div className="p-6 rounded-2xl bg-card border border-border/50">
                {currentQuestion && currentQuestion.type === 'quiz' && Array.isArray(currentQuestion.options) ? (
                  <div>
                    <h3 className="text-lg font-semibold mb-6">{currentQuestion.q}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {currentQuestion.options.map(opt => (
                        <Button key={opt} variant="outline" size="lg" onClick={() => handleQuizAnswer(opt)}
                          className="h-14 text-base" data-testid={`quiz-opt-${opt}`}
                        >
                          {opt}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-lg font-semibold">All questions answered!</p>
                    <p className="text-muted-foreground">Score: {score}/{totalQuestions}</p>
                  </div>
                )}
              </div>
            ) : mission.type === 'match-capitals' ? (
              <div className="p-6 rounded-2xl bg-card border border-border/50">
                <h3 className="text-sm font-semibold mb-4">Click a state, then click its capital to match:</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-xs text-muted-foreground font-semibold uppercase">States</h4>
                    {matchState.states.map(s => (
                      <Button key={s.id} variant={matchState.selected === s.id ? 'default' : answered.includes(s.id) ? 'ghost' : 'outline'}
                        onClick={() => !answered.includes(s.id) && handleCapitalMatch(s.id, false)}
                        className={`w-full justify-start text-sm ${answered.includes(s.id) ? 'opacity-50' : ''}`}
                        disabled={answered.includes(s.id)}
                      >
                        <MapPin className="w-3 h-3 mr-2" />{s.name}
                      </Button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-xs text-muted-foreground font-semibold uppercase">Capitals</h4>
                    {sortedCapitals.map(s => (
                      <Button key={`cap-${s.id}`} variant="outline"
                        onClick={() => handleCapitalMatch(s.id, true)}
                        className="w-full justify-start text-sm"
                        disabled={answered.includes(s.id) || !matchState.selected}
                      >
                        {s.capital}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-border/50 bg-card">
                <canvas ref={canvasRef} width={600} height={520} className="w-full cursor-pointer"
                  onClick={handleCanvasClick} onMouseMove={handleCanvasMove} data-testid="geography-canvas"
                />
              </div>
            )}

            {/* Current question prompt */}
            {currentQuestion && mission.type !== 'quiz' && mission.type !== 'match-capitals' && (
              <div className="mt-3 p-3 rounded-xl bg-primary/10 border border-primary/20 text-center">
                <span className="text-sm font-semibold">{currentQuestion.prompt}</span>
              </div>
            )}

            {/* Feedback */}
            <AnimatePresence>
              {feedback && (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className={`mt-3 p-3 rounded-xl text-center text-sm font-medium ${
                    feedback.type === 'correct' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                    'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}
                >
                  {feedback.text}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-3">Progress</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Correct</span><span className="font-mono text-green-400">{score}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono">{totalQuestions}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Found</span><span className="font-mono">{answered.length}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={resetMission} className="w-full mt-3" data-testid="reset-geo-btn">
                <RotateCcw className="w-3 h-3 mr-2" /> Reset
              </Button>
            </div>

            <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
              <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
                <Info className="w-3 h-3" /> Geography
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                India has 28 states and 8 union territories. Major rivers include Ganga, Yamuna, Godavari,
                Krishna, and Narmada. The world has 7 continents: Asia, Africa, Europe, North America,
                South America, Australia, and Antarctica.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
